import type { OrganizationDetail } from '~/common/types';
import { getAxiosClient } from './common';

const axios = getAxiosClient();

export function sortMembers(members: any[]) {
  const _members = [...members];
  return _members.sort((a, b) => {
    return a?.user?.name?.localeCompare(b?.user?.name);
  });
}

export async function newOrganization() {
  const { data } = await axios({
    method: 'POST',
    url: `/v1/organizations`,
  });
  return data;
}

export async function getOrganization(organizationId: string) {
  const { data } = await axios({
    method: 'GET',
    url: `/v1/organizations/${organizationId}`,
  });
  return data;
}

export async function editOrganization(organizationId: string, edits: any) {
  const { data } = await axios({
    method: 'PUT',
    url: `/v1/organizations/${organizationId}`,
    data: edits,
  });
  return data;
}

export async function editRole(organizationId: string, roleId: string, edits: any) {
  const { data } = await axios({
    method: 'PUT',
    url: `/v1/organizations/${organizationId}/roles/${roleId}`,
    data: edits,
  });
  return data;
}

export interface MembersResponse {
  orgMembers: OrganizationDetail['members'];
  guestMembers: Array<OrganizationDetail['members'][1] & { projects: Array<{ name: string }> }>;
  pendingMembers: OrganizationDetail['members'];
  cgMembers: OrganizationDetail['members'];
}

export async function getMembers(organizationId: string): Promise<MembersResponse> {
  const { data } = await axios({
    method: 'GET',
    url: `/v1/organizations/${organizationId}/members`,
  });
  return {
    orgMembers: sortMembers(data.orgMembers),
    guestMembers: sortMembers(data.guestMembers),
    pendingMembers: sortMembers(data.pendingMembers),
    cgMembers: sortMembers(data.cgMembers),
  };
}

export async function addMember(organizationId: string, memberData: any) {
  const { data } = await axios({
    method: 'POST',
    url: `/v1/organizations/${organizationId}/members`,
    data: memberData,
  });
  return data;
}

export async function editMember(organizationId: string, memberId: string, memberData: any) {
  const { data } = await axios({
    method: 'PUT',
    url: `/v1/organizations/${organizationId}/members/${memberId}`,
    data: memberData,
  });
  return data;
}

export async function resendInvite(organizationId: string, memberId: string) {
  const { data } = await axios({
    method: 'GET',
    url: `/v1/organizations/${organizationId}/members/${memberId}/resendInvite`,
  });
  return data;
}

export async function addFile(orgId: string, file: any) {
  const { data } = await axios({
    method: 'POST',
    url: `/v1/organizations/${orgId}/files`,
    data: file,
  });
  return data;
}

export type MyOrganization = {
  id: string;
  name: string;
  plan: string;
  planStatus: string;
  regions: string[];
  telcoAccess: string;
  zoningAccess: string;
  mobileReportType: string;
  orgMemberRole: string;
  specialDatasets: string[];
  specialFeatures: string[];
};

export type GetMyOrganizationsResponse = {
  items: MyOrganization[];
  hasMore: boolean;
};

export async function getMyOrganizations(search = '', page = 0, pageSize = 25): Promise<GetMyOrganizationsResponse> {
  const { data } = await axios({
    method: 'GET',
    url: '/v1/organizations/mine',
    params: { search, page, pageSize },
  });
  return data;
}

export async function getMyOrgTelcoData(organizationId: string, token: string) {
  const { data } = await axios({
    method: 'GET',
    url: `/v1/organizations/${organizationId}/getMyOrgTelcoData`,
    ...(token
      ? {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      : {}),
  });
  return data;
}
